import { useReviewPeriodLock } from '../workspace/reviewPeriod';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  Database,
  Loader2,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Search,
  Circle,
  Upload,
} from 'lucide-react';
import {
  apiErrorMessage,
  getFacilityIngestionCenter,
  replayFacilityDelivery,
} from '../api/abfApi';
import { ActionLocalFeedback, type ActionFeedbackState } from '../components/ActionFeedback';
import { PrerequisiteAction } from '../components/PrerequisiteAction';
import { fmtDate, fmtDateTime, shortStorageUri } from '../presentation/format';
import type {
  AbfIngestionCenterReadModel,
  AbfIngestionDelivery,
  AbfMappingDefinition,
} from '../types/ingestionCenter';

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(310px, 1fr);
  gap: 20px;
  margin-top: 20px;
  @media (max-width: 1020px) { grid-template-columns: 1fr; }
`;

const Column = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Panel = styled.section`
  padding: clamp(18px, 2vw, 28px);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  background: rgba(8, 16, 30, 0.8);
  box-shadow: 0 12px 36px rgba(2, 6, 23, 0.2);
`;

const Heading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
  h2 { margin: 0; color: #f8fafc; font-size: 18px; font-weight: 650; letter-spacing: -.015em; }
  p { margin: 0.3rem 0 0; color: #94a3b8; overflow-wrap: anywhere; font-size: 13px; line-height: 1.5; }
`;

const ConnectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

const ConnectionCard = styled.div<{ $tone: 'green' | 'amber' | 'slate' }>`
  padding: 20px;
  border: 1px solid ${p => p.$tone === 'green' ? 'rgba(52,211,153,.3)' : p.$tone === 'amber' ? 'rgba(251,191,36,.28)' : 'rgba(148,163,184,.14)'};
  border-radius: 14px;
  background: ${p => p.$tone === 'green' ? 'rgba(6,78,59,.13)' : p.$tone === 'amber' ? 'rgba(120,53,15,.1)' : 'rgba(15,23,42,.45)'};
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  svg { flex-shrink: 0; }
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 650;
`;

const Eyebrow = styled.div`
  margin-bottom: 0.28rem;
  color: #9aacbf;
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Small = styled.div`
  margin-top: 0.4rem;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.45;
`;

const Status = styled.span<{ $tone: 'green' | 'amber' | 'red' | 'blue' | 'slate' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.45rem;
  border: 1px solid ${p => ({
    green: 'rgba(52,211,153,.35)', amber: 'rgba(251,191,36,.35)', red: 'rgba(248,113,113,.38)',
    blue: 'rgba(56,189,248,.35)', slate: 'rgba(148,163,184,.2)',
  }[p.$tone])};
  border-radius: 999px;
  color: ${p => ({ green: '#6ee7b7', amber: '#fcd34d', red: '#fca5a5', blue: '#7dd3fc', slate: '#cbd5e1' }[p.$tone])};
  background: rgba(15, 23, 42, 0.45);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.75rem;
`;

const Preview = styled.div`
  overflow: auto;
  max-height: 280px;
  margin-top: 0.7rem;
  border: 1px solid rgba(148,163,184,.13);
  border-radius: 10px;
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 0.48rem; border-bottom: 1px solid rgba(148,163,184,.1); text-align: left; white-space: nowrap; }
  th { position: sticky; top: 0; background: #0b1628; color: #7dd3fc; }
  td { color: #cbd5e1; }
`;

const DeliveryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 540px; overflow: auto;
`;

const DeliveryCard = styled.button<{ $selected: boolean }>`
  width: 100%;
  padding: 16px;
  box-shadow: ${p => p.$selected ? 'inset 3px 0 #7dd3fc' : 'none'};
  &:hover { border-color: #607993; }
  border: 1px solid ${p => p.$selected ? 'rgba(56,189,248,.48)' : 'rgba(148,163,184,.14)'};
  border-radius: 11px;
  background: ${p => p.$selected ? 'rgba(14,116,144,.13)' : 'rgba(15,23,42,.42)'};
  color: inherit;
  cursor: pointer;
  text-align: left;
`;

const DeliveryTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  strong { color: #e2e8f0; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; min-width: 0; }
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.55rem;
  margin-top: 0.7rem;
  @media (max-width: 680px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const Metric = styled.div`
  padding: 0.65rem;
  border: 1px solid rgba(148,163,184,.13);
  border-radius: 9px;
  background: rgba(15,23,42,.4);
  strong { display: block; margin-top: 8px; color: #f8fafc; font-variant-numeric: tabular-nums; font-size: 22px; font-weight: 600; overflow-wrap: anywhere; }
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Step = styled.div`
  position: relative;
  & + &::before { content: ''; position: absolute; left: 17px; top: -9px; height: 9px; border-left: 1px solid #3b506a; }
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.55rem;
  padding: 0.6rem;
  border: 1px solid transparent;
  border-radius: 9px;
  background: rgba(15,23,42,.36);
`;

const Empty = styled.div`
  padding: 1.5rem 0.75rem;
  color: #94a3b8;
  text-align: center;
  font-size: 13px;
`;

const IngestionSurface = styled.div`
  color: #dce6f2; font-size: 14px; line-height: 1.5;
  button, input, textarea { font-family: inherit; }
  button:focus-visible, input:focus-visible, textarea:focus-visible, summary:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; }
  button:disabled { cursor: not-allowed; }
  input:not([type='file']), textarea { font-size: 14px; min-height: 42px; box-sizing: border-box; }
  .page-heading { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; margin: 24px 0; }
  .page-heading h2 { color: #f1f5f9; font-size: 26px; margin: 0; font-weight: 650; letter-spacing: -.03em; }
  .page-heading p { color: #a2b3c9; font-size: 14px; margin: 6px 0 0; }
  .header-actions { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
  .history-search { display: flex; align-items: center; gap: 8px; padding: 0 12px; margin: 0 0 12px; background: #091321; border: 1px solid #33465d; border-radius: 9px; }
  .history-search input { width: 100%; min-width: 0; background: transparent; color: #e2e8f0; border: 0; }
  .history-filters { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 16px; }
  .history-filters button { padding: 6px 10px; color: #b9c8d9; background: #111e30; border: 1px solid #33465d; border-radius: 7px; font-size: 12px; cursor: pointer; }
  .history-filters button[aria-pressed='true'] { border-color: #7dd3fc; color: #e0f2fe; background: #17334b; }
  .selected-file { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
  .source-record { border-top: 1px solid #29384d; margin-top: 18px; padding-top: 16px; }
  .source-record summary { cursor: pointer; color: #a8c3dd; font-size: 13px; }
  @media(max-width:700px) { .page-heading { align-items: flex-start; } }
  @media(prefers-reduced-motion:reduce) { * { transition: none; } }
`;

const needsAttention = (delivery: AbfIngestionDelivery) =>
  !['DISCARDED', 'SUPERSEDED'].includes(String(delivery.authority_status)) && (['FAILED', 'QUARANTINED'].includes(String(delivery.delivery_state).toUpperCase())
  || (Number(delivery.rows_failed || 0) > 0 && delivery.authority_status !== 'RELEASED')
  || (delivery.mapping_status === 'NEEDS_REVIEW' && delivery.authority_status !== 'RELEASED'));

interface Props {
  facilityId: number;
  servicerName?: string | null;
  userRole?: string | null;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
  onReviewPeriod?: (delivery: AbfIngestionDelivery) => Promise<void>;
  openingPeriod?: boolean;
  /** Opens Period review, the one place a tape is received and released (F-S2-UI-003). */
  onAddTape?: () => void;
}

const roleAliases: Record<string, string> = {
  PM: 'PORTFOLIO_MANAGER', RISK: 'RISK_MANAGER', OPERATIONS: 'OPERATIONS_ANALYST',
};

const title = (value: unknown) => String(value || '').replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\S/g, match => match.toUpperCase());
const deliveryChannelLabel = (value: unknown) => String(value || '').toUpperCase() === 'UI_UPLOAD'
  ? 'Interactive upload'
  : title(value);
const number = (value: unknown) => value === null || value === undefined ? '—' : new Intl.NumberFormat('en-US').format(Number(value));
const bytes = (value: unknown) => {
  const amount = Number(value || 0);
  return amount >= 1_000_000 ? `${(amount / 1_000_000).toFixed(1)} MB` : `${Math.max(0, Math.round(amount / 1000))} KB`;
};
const shortHash = (value: string | null | undefined) => value ? `${value.slice(0, 12)}…` : 'Not available';
const dateTime = (value: string | null | undefined) => fmtDateTime(value, 'Not available');

const tone = (value: string | null | undefined): 'green' | 'amber' | 'red' | 'blue' | 'slate' => {
  const state = String(value || '').toUpperCase();
  if (['AVAILABLE', 'READY', 'RELEASED', 'SUCCEEDED', 'PASSED', 'UNCHANGED'].includes(state)) return 'green';
  if (['QUARANTINED', 'CHANGED', 'CONFIGURED_NOT_VALIDATED', 'STUB_NOT_VALIDATED'].includes(state)) return 'amber';
  if (['FAILED'].includes(state)) return 'red';
  if (['PROCESSING', 'RUNNING', 'RECEIVED'].includes(state)) return 'blue';
  return 'slate';
};

const parseMapping = (value: AbfIngestionDelivery['mapping_json']): AbfMappingDefinition => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value) as AbfMappingDefinition; } catch { return {}; }
};

const detailText = (raw: string | null | undefined) => {
  if (!raw) return '';
  try { return String((JSON.parse(raw) as { detail?: string }).detail || ''); } catch { return raw; }
};

const STORAGE_URI = /\b(?:s3|gs|abfs|file):\/\/[^\s"']+/gi;
/** Object keys stay; the bucket and account id do not belong on a facility screen. */
const maskStorageUris = (text: string) => text.replace(STORAGE_URI, match => shortStorageUri(match));
const containsStorageUri = (text: string) => { STORAGE_URI.lastIndex = 0; return STORAGE_URI.test(text); };

/**
 * Source-connection states in product words. The backend note for a known state is an
 * engineering status; a user needs to know whether the source is connected and who can fix it.
 */
const connectionPresentation = (status: string | null | undefined, backendDetail: string): { label: string; detail: string } => {
  const state = String(status || '').toUpperCase();
  if (state === 'CONFIGURED_NOT_VALIDATED' || state === 'STUB_NOT_VALIDATED') {
    return { label: 'Configured · awaiting validation', detail: 'Connected object store. Deliveries are validated before they appear here.' };
  }
  if (state === 'NOT_CONFIGURED') return { label: 'Not connected', detail: 'Ask an administrator to connect this source.' };
  return { label: title(state), detail: backendDetail };
};

// Preview the same logical records the intake accepts, including quoted commas and newlines.

const IngestionWorkspace: React.FC<Props> = ({
  facilityId, userRole, onFeedback, onReviewPeriod, openingPeriod, onAddTape,
}) => {
  const [center, setCenter] = useState<AbfIngestionCenterReadModel | null>(null);
  const [params, setParams] = useSearchParams();
  const [selectedManifestId, setSelectedManifestId] = useState<number | null>(() => Number(params.get('delivery')) || null);
  const selectDelivery = (id: number) => {
    setSelectedManifestId(id);
    const copy = new URLSearchParams(params); copy.set('delivery', String(id)); setParams(copy, { replace: true });
    requestAnimationFrame(() => evidenceRef.current?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
  };
  const [historySearch, setHistorySearch] = useState('');
  const [historyFilter, setHistoryFilter] = useState<'all' | 'attention' | 'released'>('all');
  const evidenceRef = useRef<HTMLElement>(null);
  const [busy, setBusy] = useState<'load' | 'replay' | null>('load');
  useReviewPeriodLock(busy === 'replay');
  const [localFeedback, setLocalFeedback] = useState<ActionFeedbackState | null>(null);

  const load = useCallback(async (notify = false) => {
    setBusy('load');
    try {
      const result = await getFacilityIngestionCenter(facilityId);
      setCenter(result);
      setLocalFeedback(current => current?.tone === 'error' ? null : current);
      setSelectedManifestId(current => current ?? result.deliveries[0]?.manifest_id ?? null);
      if (notify) {
        const feedback = { tone: 'success' as const, message: 'Delivery, mapping and job lineage refreshed.' };
        setLocalFeedback(feedback); onFeedback(feedback);
      }
    } catch (error) {
      const feedback = { tone: 'error' as const, message: apiErrorMessage(error, 'Unable to load the Ingestion Center.') };
      setLocalFeedback(feedback); onFeedback(feedback);
    } finally { setBusy(null); }
  }, [facilityId, onFeedback]);

  useEffect(() => { load(); }, [load]);

  const normalizedRole = roleAliases[String(userRole || '').toUpperCase()] || String(userRole || '').toUpperCase();
  const canOperate = ['ADMIN', 'PLATFORM_ADMIN', 'PORTFOLIO_MANAGER', 'TRADER', 'RISK_MANAGER', 'OPERATIONS_ANALYST'].includes(normalizedRole);
  const selected = center?.deliveries.find(item => item.manifest_id === selectedManifestId) || center?.deliveries[0] || null;
  const mapping = useMemo(() => parseMapping(selected?.mapping_json || null), [selected]);
  const columns = mapping.columns || [];
  const unmapped = mapping.unmappedColumns || [];
  const showMappingStudio = Boolean(selected && (selected.schemaDrift !== 'UNCHANGED' || unmapped.length > 0));
  const latestJob = selected?.jobs?.[0];

  const replay = async () => {
    if (!selected) return;
    setBusy('replay');
    try {
      const result = await replayFacilityDelivery(facilityId, selected.manifest_id);
      await load();
      selectDelivery(result.manifest_id);
      const feedback = { tone: 'success' as const, message: result.message || 'The retained delivery was replayed.' };
      setLocalFeedback(feedback); onFeedback(feedback);
    } catch (error) {
      const feedback = { tone: 'error' as const, message: apiErrorMessage(error, 'The retained delivery could not be replayed.') };
      setLocalFeedback(feedback); onFeedback(feedback);
    } finally { setBusy(null); }
  };

  if (!center && busy === 'load') return <Layout><Panel><Empty>Loading source and delivery evidence…</Empty></Panel></Layout>;
  if (!center) return <Panel><Empty><h2>Delivery evidence could not be loaded</h2><p>{localFeedback?.message}</p><PrerequisiteAction onClick={() => void load()}>Try again</PrerequisiteAction></Empty></Panel>;

  const filteredDeliveries = center.deliveries.filter(delivery => {
    const matches = `${delivery.original_filename} ${delivery.business_date} ${delivery.vendor_name || ''} ${delivery.servicer_name || ''}`.toLowerCase().includes(historySearch.toLowerCase());
    return matches && (historyFilter === 'attention' ? needsAttention(delivery) : historyFilter === 'released' ? delivery.authority_status === 'RELEASED' : true);
  });
  const attentionCount = center.deliveries.filter(needsAttention).length;
  return (
    <IngestionSurface>
      <header className="page-heading">
        <div><Eyebrow>Collateral operations</Eyebrow><h2>Ingestion Center</h2><p>Delivery sources and the evidence behind every servicer delivery. Tapes are received and released in Period review.</p></div>
        <div className="header-actions"><Status $tone={attentionCount ? 'amber' : 'slate'}>{attentionCount ? `${attentionCount} deliveries need attention` : `${center.deliveries.length} retained deliveries`}</Status>
          <PrerequisiteAction disabled={Boolean(busy)} onClick={() => void load(true)}><RefreshCw size={16} />Refresh</PrerequisiteAction>
        </div>
      </header>
      <ActionLocalFeedback feedback={localFeedback} />
      <Panel style={{ marginTop: 16 }}>
        <Heading><div><h2>Delivery sources</h2><p>Connection status and available intake methods for this facility.</p></div><ShieldCheck size={20} color="#7dd3fc" /></Heading>
          <ConnectionGrid>
            {center.connections.map(connection => (
              <ConnectionCard key={connection.type} $tone={tone(connection.status) === 'green' ? 'green' : tone(connection.status) === 'amber' ? 'amber' : 'slate'}>
                <Eyebrow>{title(connection.type)}</Eyebrow>
                <CardTitle>{connection.type === 'S3' ? <Cloud size={15} /> : connection.type === 'SNOWFLAKE' ? <Database size={15} /> : <Upload size={15} />}{connection.name}</CardTitle>
                <div style={{ marginTop: 8 }}><Status $tone={tone(connection.status)}>{connectionPresentation(connection.status, connection.detail).label}</Status></div>
                <Small>{connectionPresentation(connection.status, connection.detail).detail}</Small><Small>{connection.boundary}</Small>
              </ConnectionCard>
            ))}
          </ConnectionGrid>
      </Panel>
      <Layout>
        <Column>
        <Panel>
          <Heading><div><h2>Adding a tape</h2><p>A tape is added to its collection period in Period review, by file (CSV or XLSX), by grid or by paste. The as-of date places it in the period; the file is retained here with its manifest, checksum and lineage whichever way it arrived.</p></div><Upload size={18} color="#38bdf8" /></Heading>
          {onAddTape && <ButtonRow><PrerequisiteAction primary disabled={Boolean(busy)} onClick={onAddTape}><Upload size={14} /> Add a tape in Period review</PrerequisiteAction></ButtonRow>}
        </Panel>

        {selected && (
          <Panel ref={evidenceRef} style={{ scrollMarginTop: 24 }}>
            <Heading><div><h2>Delivery evidence</h2><p>{selected.original_filename} · business date {fmtDate(selected.business_date)}</p></div><Status $tone={tone(selected.delivery_state)}>{title(selected.delivery_state)}</Status></Heading>
            <MetricGrid>
              <Metric><Eyebrow>Rows received</Eyebrow><strong>{number(selected.rows_received)}</strong></Metric>
              <Metric><Eyebrow>Accepted</Eyebrow><strong>{number(selected.rows_inserted)}</strong></Metric>
              <Metric><Eyebrow>Rejected / skipped</Eyebrow><strong>{number(selected.rows_failed == null && selected.rows_skipped == null ? null : Number(selected.rows_failed || 0) + Number(selected.rows_skipped || 0))}</strong></Metric>
              <Metric><Eyebrow>Data quality</Eyebrow><strong>{title(selected.dq_status || selected.delivery_state)}</strong></Metric>
            </MetricGrid>
            <details className="source-record"><summary>Source integrity and lineage</summary>
            <Small>Raw authority: immutable {title(selected.storage_provider)} object · checksum {shortHash(selected.sha256)} · {bytes(selected.byte_size)}</Small>
            <Small>Collateral authority: {selected.authority_status ? title(selected.authority_status) : 'No tape authority created'} · schema {shortHash(selected.schema_fingerprint)} · drift {title(selected.schemaDrift)}</Small>
            {selected.authority_status === 'DISCARDED' && <Small>Discarded by {selected.discarded_by || '—'}{selected.discard_reason ? ` — “${selected.discard_reason}”` : ''}. It stays on record and can never be released; send the file again to read it as a new candidate.</Small>}
            <Small>Received by {selected.tape_created_by || 'not recorded'}{selected.released_by ? ` · released by ${selected.released_by}` : ' · checker release pending'}</Small>
            </details>
            {onReviewPeriod && <ButtonRow><PrerequisiteAction disabled={Boolean(openingPeriod || busy)} onClick={() => void onReviewPeriod(selected)}>
              {openingPeriod ? 'Opening period…' : 'Review this period'}
            </PrerequisiteAction><Small>Resolve mapping or row issues, release the tape, add the certificate and continue to independent approval.</Small></ButtonRow>}
            {selected.error_summary && <Small style={{ color: '#fca5a5' }}><AlertTriangle size={13} /> {selected.error_summary}</Small>}
            <ButtonRow>
              <PrerequisiteAction
                disabled={!canOperate || Boolean(busy)}
                disabledReason={!canOperate ? 'A portfolio, risk, operations or administrator role is required to replay retained evidence.' : busy ? 'Another delivery action is already processing.' : undefined}
                onClick={replay}
              >{busy === 'replay' ? <Loader2 size={14} /> : <RotateCcw size={14} />} Replay retained source</PrerequisiteAction>
            </ButtonRow>
            </Panel>
        )}

        {selected && showMappingStudio && (
          <Panel>
            <Heading><div><h2>Column mapping</h2><p>Review how source columns map to loan fields. New or changed mappings need an independent decision in Period review.</p></div><Status $tone={unmapped.length ? 'amber' : 'green'}>{unmapped.length ? `${unmapped.length} need review` : 'Complete coverage'}</Status></Heading>
            <MetricGrid>
              <Metric><Eyebrow>Profile</Eyebrow><strong>{selected.profile_name || 'Detected profile'}</strong></Metric>
              <Metric><Eyebrow>Coverage</Eyebrow><strong>{number(mapping.mappedCount ?? columns.filter(column => column.canonicalField).length)} / {number(mapping.headerCount ?? columns.length)}</strong></Metric>
              <Metric><Eyebrow>Confidence</Eyebrow><strong>{selected.confidence_score == null ? '—' : `${Number(selected.confidence_score).toFixed(1)}%`}</strong></Metric>
              <Metric><Eyebrow>Schema drift</Eyebrow><strong>{title(selected.schemaDrift)}</strong></Metric>
            </MetricGrid>
            {columns.length > 0 && <Preview><table><thead><tr><th>Source column</th><th>Canonical business field</th><th>Required</th><th>Confidence</th><th>Decision</th></tr></thead><tbody>{columns.map(column => <tr key={column.sourceColumn}><td>{column.sourceColumn}</td><td>{column.canonicalField ? title(column.canonicalField) : 'Needs mapping'}</td><td>{column.required ? 'Required' : 'Optional'}</td><td>{column.confidenceScore == null ? '—' : `${Number(column.confidenceScore) * (Number(column.confidenceScore) <= 1 ? 100 : 1)}%`}</td><td>{column.canonicalField ? 'Mapped' : 'Review'}</td></tr>)}</tbody></table></Preview>}
          </Panel>
        )}
      </Column>

      <Column>
        <Panel>
          <Heading><div><h2>Delivery history</h2><p>Newest immutable source deliveries for this facility.</p></div><ShieldCheck size={18} color="#34d399" /></Heading>
          <label className="history-search"><Search size={16} color="#8caac5" /><input aria-label="Search delivery history" placeholder="Find a file, date or servicer" value={historySearch} onChange={event => setHistorySearch(event.target.value)} /></label>
          <div className="history-filters" aria-label="Filter delivery history">
            <button type="button" aria-pressed={historyFilter === 'all'} onClick={() => setHistoryFilter('all')}>All · {center.deliveries.length}</button>
            <button type="button" aria-pressed={historyFilter === 'attention'} onClick={() => setHistoryFilter('attention')}>Needs attention · {attentionCount}</button>
            <button type="button" aria-pressed={historyFilter === 'released'} onClick={() => setHistoryFilter('released')}>Released</button>
          </div>
          {center.deliveries.length === 0 ? <Empty>No retained deliveries yet. Add a tape in Period review to start its review history.</Empty> : (
            <DeliveryList>{filteredDeliveries.map(delivery => (
              <DeliveryCard aria-pressed={delivery.manifest_id === selected?.manifest_id} key={delivery.manifest_id} $selected={delivery.manifest_id === selected?.manifest_id} disabled={Boolean(busy)} onClick={() => selectDelivery(delivery.manifest_id)}>
                <DeliveryTop><strong>{delivery.original_filename}</strong><Status $tone={tone(delivery.delivery_state)}>{title(delivery.delivery_state)}</Status></DeliveryTop>
                <Small>{fmtDate(delivery.business_date)} · {deliveryChannelLabel(delivery.delivery_channel)} · {title(delivery.storage_provider)}</Small>
                <Small>{delivery.schemaDrift === 'CHANGED' ? 'Columns changed since the prior delivery' : delivery.schemaDrift === 'UNCHANGED' ? 'Matches the previous column layout' : 'First delivery with this column layout'}</Small>
              </DeliveryCard>
            ))}</DeliveryList>
          )}
          {center.deliveries.length > 0 && !filteredDeliveries.length && <Empty>No deliveries match these filters.<ButtonRow><PrerequisiteAction onClick={() => { setHistorySearch(''); setHistoryFilter('all'); }}>Clear filters</PrerequisiteAction></ButtonRow></Empty>}
        </Panel>

        <Panel>
          <Heading><div><h2>Processing history</h2><p>{selected ? selected.original_filename : 'Select a delivery to inspect its job progress and lineage.'}</p></div>{latestJob?.status === 'RUNNING' ? <Loader2 size={18} color="#38bdf8" /> : latestJob && tone(latestJob.status) === 'green' ? <CheckCircle2 size={18} color="#34d399" /> : latestJob && tone(latestJob.status) === 'red' ? <AlertTriangle size={18} color="#f87171" /> : <Circle size={18} color="#94a3b8" />}</Heading>
          {!latestJob ? <Empty>Select or receive a delivery to inspect its processing stages.</Empty> : (
            <Timeline>
              {(latestJob.steps || []).map(step => {
                const failed = tone(step.status) === 'red';
                const message = failed
                  ? step.errorDetail || detailText(step.detailJson) || title(step.status)
                  : detailText(step.detailJson) || step.errorDetail || title(step.status);
                return <Step key={step.id}>{tone(step.status) === 'green' ? <CheckCircle2 size={15} color="#34d399" /> : failed ? <AlertTriangle size={15} color="#f87171" /> : <Loader2 size={15} color="#38bdf8" />}<div><CardTitle>{title(step.stepName)}</CardTitle><Small title={containsStorageUri(message) ? message : undefined}>{maskStorageUris(message)}</Small></div></Step>;
              })}
              <Small>Run {title(latestJob.status)} · started {dateTime(latestJob.startedAt)}{latestJob.completedAt ? ` · completed ${dateTime(latestJob.completedAt)}` : ''}</Small>
            </Timeline>
          )}
        </Panel>

        <Panel>
          <Heading><div><h2>Authority boundary</h2><p>{center.authorityBoundary}</p></div><Database size={18} color="#60a5fa" /></Heading>
          <Small>Release, borrowing-base approval, funding and official IBOR posting remain in their existing independently controlled services.</Small>
        </Panel>
      </Column>
      </Layout>
    </IngestionSurface>
  );
};

export default IngestionWorkspace;
